<template>
  <div class="debt-card-wrapper">
    <div class="debt-card" :class="{ 'debt-card--eudora': brand === 'eudora' }">
      <div class="card-header">
        <div class="card-brand">
          <span class="card-brand__icon-slot" aria-hidden="true">
            <img
              :src="brand === 'eudora' ? '/img/eudora-icone-card.png' : '/img/boticario-icone-card.png'"
              alt=""
              class="card-brand__icon"
            />
          </span>
          <strong class="card-brand__name">{{ brand === 'eudora' ? 'Eudora' : 'Boticário' }}</strong>
        </div>

        <div v-if="documentKind" class="card-document">
          <strong class="card-document__title">{{ documentHeading }}</strong>
          <span class="card-document__id">{{ documentKind }}: {{ formattedDocument }}</span>
        </div>

        <div class="card-field">
          <span class="card-field__label">{{ labelValorOriginal }}</span>
          <strong class="card-field__value">{{ formatFloatToReal(parseFloat(originalValue)) }}</strong>
        </div>

        <div class="card-field">
          <span class="card-field__label">{{ labelVencimentoOriginal }}</span>
          <strong class="card-field__value">{{ formatDate(earliestDueDate) }}</strong>
        </div>

        <div class="card-field card-field--titles">
          <span class="card-field__label">Título(s):</span>
          <span class="card-field__titles-value">
            {{ isExpanded ? installments : truncated(installments, 27) }}
          </span>
          <button
            v-if="installments && installments.length > 27"
            @click="isExpanded = !isExpanded"
            class="btn-expand-titles"
            :title="isExpanded ? 'Recolher' : 'Expandir'"
          >
            <i :class="isExpanded ? 'fa fa-chevron-up' : 'fa fa-chevron-down'"></i>
          </button>
        </div>
      </div>

      <div class="card-body">
        <hr class="card-body-divider" />

        <div class="d-flex justify-content-between align-items-start mb-2 mt-2">
          <div>
            <p class="mb-0 de-por" v-if="updatedValueFormatted">
              <span class="de-por__label">Valor atualizado:</span>
              <strike class="de-por__value">R$ {{ updatedValueFormatted }}</strike>
            </p>
            <p class="mb-0 economize" v-if="economyValue > 0">
              Economize <strong>{{ formatFloatToReal(economyValue) }}</strong>
            </p>
            <p class="valores m-0" v-if="bestOffer">
              pagando à vista <span class="price-accent">{{ formatFloatToReal(parseFloat(bestOffer.installmentValue)) }}</span>
            </p>
            <p class="mb-0 small">para pagamento até {{ currentDate }}</p>
          </div>
          <div class="text-right ml-2">
            <span class="discount-pill" v-if="discountPercentual > 0">
              <span class="label">Desconto</span>
              <span class="value" :style="{ backgroundColor: brandColor }">
                {{ discountPercentual }}%
              </span>
            </span>
          </div>
        </div>

        <p class="mb-3 small" v-if="hasInstallments">
          ou parcelado em até {{ maxInstallmentNumber }}x
        </p>

        <button
          class="btn btn-block text-white btn-negociar"
          :class="{ 'btn-negociar--eudora': brand === 'eudora' }"
          @click="$emit('negociar', debt)"
          style="cursor: pointer !important"
        >
          NEGOCIAR
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { formatData } from '@/utils/helpers';
import { brandAccentColor } from '@/utils/brandColors';

export default {
  name: 'DebtCard',
  props: {
    debt: { type: Object, required: true },
    brand: { type: String, default: 'boticario' },
  },
  data: () => ({ isExpanded: false }),
  computed: {
    installments() { return this.debt.installments; },
    originalValue() { return this.debt.originalValue; },
    earliestDueDate() { return this.debt.earliestDueDate; },
    updatedValueFormatted() { return this.debt.updatedValueFormatted; },
    offers() {
      return this.brand === 'eudora'
        ? (this.debt.offersEudora || [])
        : (this.debt.offersBoticario || []);
    },
    bestOffer() { return this.offers[0] || null; },
    hasInstallments() { return this.offers.length > 1; },
    maxInstallmentNumber() { return this.offers[this.offers.length - 1]?.installmentNumber; },
    brandColor() {
      return brandAccentColor(this.brand);
    },
    labelValorOriginal() {
      return this.brand === 'eudora' ? 'Valor original da dívida:' : 'Valor original:';
    },
    labelVencimentoOriginal() {
      return this.brand === 'eudora' ? 'Vencimento original da dívida:' : 'Vencimento original:';
    },
    documentDigits() {
      return String(this.debt.document || '').replace(/\D/g, '');
    },
    documentKind() {
      const type = String(this.debt.documentType || '').trim().toUpperCase();
      if (type === 'CPF' || type === 'CNPJ') return type;
      if (this.documentDigits.length === 14) return 'CNPJ';
      if (this.documentDigits.length === 11) return 'CPF';
      return '';
    },
    documentHeading() {
      if (this.documentKind === 'CNPJ') return 'Débitos CNPJ (Pessoa jurídica)';
      if (this.documentKind === 'CPF') return 'Débitos CPF (Pessoa física)';
      return '';
    },
    formattedDocument() {
      const d = this.documentDigits;
      if (this.documentKind === 'CNPJ') {
        const padded = d.padStart(14, '0').slice(-14);
        return padded.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
      }
      if (this.documentKind === 'CPF') {
        const padded = d.padStart(11, '0').slice(-11);
        return padded.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4');
      }
      return d;
    },
    cardStyle() {
      return {};
    },
    logoSrc() { return this.brand === 'eudora' ? '/img/logo-eudora.png' : '/img/logo-boticario.png'; },
    logoStyle() { return { width: '100px' }; },
    currentDate() { return new Date().toLocaleDateString('pt-BR'); },
    discountPercentual() {
      if (!this.bestOffer || !this.debt.updatedValue || this.debt.updatedValue <= 0) return 0;
      const d = ((this.debt.updatedValue - this.bestOffer.installmentValue) / this.debt.updatedValue) * 100;
      return d > 0 ? parseFloat(d.toFixed(2)) : 0;
    },
    economyValue() {
      if (!this.bestOffer || !this.debt.updatedValue || this.debt.updatedValue <= 0) return 0;
      const economy = this.debt.updatedValue - this.bestOffer.installmentValue;
      return economy > 0 ? economy : 0;
    },
  },
  methods: {
    formatFloatToReal(value) {
      return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
    },
    formatDate(date) {
      return formatData(date, 'YYYY-MM-DD', 'L');
    },
    truncated(text, length) {
      if (!text) return '';
      return text.length > length && !this.isExpanded ? text.substring(0, length) + '...' : text;
    },
  },
};
</script>

<style scoped>
.debt-card-wrapper {
  flex-shrink: 0;
  width: 100%;
  max-width: 360px;
  box-sizing: border-box;
}

.debt-card {
  background: #fff;
  border: none;
  border-top: 4px solid #06a472;
  border-radius: 20px;
  min-width: 300px;
  max-width: 360px;
  overflow: hidden;
  box-shadow: var(--negotiation-card-shadow, 0 2px 12px rgba(0, 0, 0, 0.1));
}

.debt-card .card-header {
  background: #fff;
  border-bottom: none;
  padding: 16px 20px 4px;
  border-radius: 0;
}

.debt-card .card-body {
  padding: 6px 20px 20px;
}

.card-brand {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
}

.card-brand__icon-slot {
  flex: 0 0 32px;
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.card-brand__icon {
  width: 100%;
  height: 100%;
  max-width: 32px;
  max-height: 32px;
  object-fit: contain;
  object-position: center;
  display: block;
}
.card-brand__name {
  font-family: 'Inter', sans-serif;
  font-size: 0.95rem;
  color: #333438;
}

.card-document {
  margin: 8px 0 10px;
}

.card-document__title {
  display: block;
  font-family: 'Inter', sans-serif;
  font-size: 0.88rem;
  font-weight: 700;
  line-height: 1.35;
  color: #333438;
}

.card-document__id {
  display: block;
  margin-top: 2px;
  font-family: 'Inter', sans-serif;
  font-size: 0.82rem;
  font-weight: 400;
  line-height: 1.35;
  color: #333438;
}

.card-divider {
  margin: 10px 0;
  border-color: #e6e7e9;
}

.card-field {
  margin-bottom: 2px;
  font-family: 'Inter', sans-serif;
  font-size: 0.82rem;
  color: #333438;
}
.card-field__label {
  color: #666;
  font-weight: 400;
}
.card-field__value {
  font-weight: 600;
  color: #333438;
  margin-left: 3px;
}

.card-field--titles {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 4px 6px;
}
.card-field--titles .card-field__label {
  flex-shrink: 0;
  white-space: nowrap;
}
.card-field__titles-value {
  flex: 1 1 0;
  min-width: 0;
  font-weight: 400;
  color: #333438;
  word-break: break-word;
  line-height: 1.4;
}
.card-field--titles .btn-expand-titles {
  flex-shrink: 0;
  align-self: flex-start;
}

.economize {
  font-size: 0.82rem;
  color: #555;
  margin-bottom: 2px;
}

.de-por {
  font-family: 'Inter', sans-serif;
  font-size: 0.86rem;
  color: #555;
}

.de-por__label {
  margin-right: 4px;
}

.de-por__value {
  font-size: 0.9rem;
  color: #6b7280;
}

.valores {
  font-weight: 400 !important;
  font-size: 0.82rem;
}

.price-accent {
  display: inline-block;
  margin-left: 2px;
  color: #06a472 !important;
  font-weight: 700 !important;
  font-size: 1.12rem !important;
  letter-spacing: 0.01em;
}

.debt-card--eudora {
  border-top-color: #613a53;
}

.debt-card--eudora .price-accent {
  color: #613a53 !important;
}

.debt-card--eudora .btn-expand-titles {
  color: #613a53;
}
.debt-card--eudora .btn-expand-titles:hover {
  background-color: rgba(97, 58, 83, 0.1);
}

@media (min-width: 768px) {
  .debt-card-wrapper {
    flex: 0 1 360px;
    width: auto;
    max-width: 360px;
    min-width: 300px;
  }
}

@media (max-width: 767px) {
  .debt-card-wrapper {
    max-width: 100%;
    min-width: 0;
  }

  .debt-card {
    min-width: 100%;
    max-width: 100%;
  }
}

.card-body-divider {
  margin: 0 0 12px;
  border-color: #e6e7e9;
}

.btn-expand-titles {
  background: transparent;
  border: none;
  color: #176e53;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  transition: all 0.2s ease;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 1.4;
  min-height: 1.4em;
}
.btn-expand-titles:hover { background-color: rgba(23, 110, 83, 0.08); }
.btn-expand-titles:focus { outline: none; }
.btn-expand-titles i { font-size: 0.85rem; }

.discount-pill {
  display: flex;
  flex-direction: column;
  align-items: center;
  font-size: 0.68rem;
}
.discount-pill .label { color: #555; margin-bottom: 2px; }
.discount-pill .value {
  color: #fff;
  font-weight: bold;
  padding: 5px 12px;
  border-radius: 999px;
  font-size: 0.9rem;
}

.btn-negociar {
  background-color: #06a472 !important;
  border-color: #06a472 !important;
  border-radius: 8px !important;
  font-family: 'Inter', sans-serif;
  font-weight: 500;
  font-size: 1rem;
}
.btn-negociar:hover {
  background-color: #125c44 !important;
  border-color: #125c44 !important;
}

.btn-negociar--eudora {
  background-color: #613a53 !important;
  border-color: #613a53 !important;
}
.btn-negociar--eudora:hover {
  background-color: #4d2e42 !important;
  border-color: #4d2e42 !important;
}
</style>
